import { dateToTimeStamp, isEmptyArr, message } from '@utils/index';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import { crmConstants } from 'modules/crm';

import { useAppSelector } from '../../../hooks/reduxHooks';
import { baseErrorHandler } from '../../../queries/baseErrorHandler';
import { showMessage } from '../../../queries/showMessage';
import { $api } from '../../../service/RequestService';
import { downloadFile } from '../../../utils/helper/downloadFile';
import { getCrmBody } from '../../../utils/helper/getSearchAndDownloadBodyAllTables';
import { CrmExportType } from '../../../utils/models/CrmExportType';
import { LeadGroup } from '../../../utils/models/LeadGroup';
import { GroupActions } from '../../../utils/models/ListViewCrm';
import { ReasonsRefusal } from '../../../utils/models/ReasonsRefusal';
import { TriggerBody, TriggerBodyPipe } from '../../../utils/models/Trigger';
import { SearchParamsCrm } from '../components/filter/FilterButton';
import { endPoints } from '../utils/constants/endPoints';
import { queryKeys } from '../utils/constants/queryKeys';

export function useCreateEditTrigger(pipeStatusId: number, onSuccess: () => void) {
  const qc = useQueryClient();

  return useMutation(
    async (data: { body: TriggerBody; all: boolean }) => {
      const res = await $api.post(`${endPoints.CRM_TRIGGERS_TASK_TRIGGER}?all=${data.all}`, data.body);

      return res.data;
    },
    {
      onSuccess: data => {
        showMessage(data.message, data.accept);
        qc.invalidateQueries([queryKeys.TRIGGERS_LIST, pipeStatusId]);
        onSuccess();
      },
      onError: baseErrorHandler
    }
  );
}

export function useCreateEditTriggerPipe(pipeStatusId: number, onSuccess: () => void) {
  const qc = useQueryClient();

  return useMutation(
    async (data: { body: TriggerBodyPipe; all: boolean }) => {
      const res = await $api.post(`${endPoints.CRM_TRIGGERS_PIPE_STATUS_TRIGGER}?all=${data.all}`, data.body);

      return res.data;
    },
    {
      onSuccess: data => {
        showMessage(data.message, data.accept);
        qc.invalidateQueries([queryKeys.TRIGGERS_LIST, pipeStatusId]);
        onSuccess();
      },
      onError: baseErrorHandler
    }
  );
}

export function useCreateEditTriggerManager(pipeStatusId: number, onSuccess: () => void) {
  const qc = useQueryClient();

  return useMutation(
    async (data: { body: TriggerBodyPipe; all: boolean }) => {
      const res = await $api.post(`${endPoints.CRM_TRIGGERS_MANAGER_TRIGGER}?all=${data.all}`, data.body);

      return res.data;
    },
    {
      onSuccess: data => {
        showMessage(data.message, data.accept);
        qc.invalidateQueries([queryKeys.TRIGGERS_LIST, pipeStatusId]);
        onSuccess();
      },
      onError: baseErrorHandler
    }
  );
}

export function useCreateEditTriggerCall(pipeStatusId: number, onSuccess: () => void) {
  const qc = useQueryClient();

  return useMutation(
    async (data: { body: TriggerBodyPipe; all: boolean }) => {
      const res = await $api.post(`${endPoints.CRM_TRIGGERS_CALL_BROJECT_TRIGGER}?all=${data.all}`, data.body);

      return res.data;
    },
    {
      onSuccess: data => {
        showMessage(data.message, data.accept);
        qc.invalidateQueries([queryKeys.TRIGGERS_LIST, pipeStatusId]);
        onSuccess();
      },
      onError: baseErrorHandler
    }
  );
}

export function useDeleteTrigger(pipeStatusId: number, triggerId: number, onSuccess: () => void) {
  const qc = useQueryClient();

  return useMutation(
    async () => {
      const res = await $api.delete(`${endPoints.CRM_TRIGGERS}/${triggerId}`);

      return res.data;
    },
    {
      onSuccess: data => {
        showMessage(data.message, data.accept);
        qc.invalidateQueries([queryKeys.TRIGGERS_LIST, pipeStatusId]);
        onSuccess();
      },
      onError: baseErrorHandler
    }
  );
}

export function useReasonsForRefusalDelete(onSuccess?: () => void) {
  const qc = useQueryClient();

  return useMutation(
    async (id: number) => {
      const res = await $api.delete(endPoints.REASONS_FOR_REFUSAL + id);

      return res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries([queryKeys.REASONS_FOR_REFUSAL]);
        qc.invalidateQueries([crmConstants.queryKeys.LEAD_FILTER_PARAMS]);

        if (onSuccess) {
          onSuccess();
        }
      },
      onError: baseErrorHandler
    }
  );
}

export function useReasonsForRefusalCreate(onSuccess?: () => void) {
  const qc = useQueryClient();

  return useMutation(
    async (data: ReasonsRefusal[]) => {
      const res = await $api.post(endPoints.REASONS_FOR_REFUSAL, data);

      return res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries([queryKeys.REASONS_FOR_REFUSAL]);
        qc.invalidateQueries([crmConstants.queryKeys.LEAD_FILTER_PARAMS]);

        if (onSuccess) {
          onSuccess();
        }
      },
      onError: baseErrorHandler
    }
  );
}

export function usePipeStatusDelete() {
  const qc = useQueryClient();

  return useMutation(
    async (data: { pipeStatusId: number; pipeId: number }) => {
      const res = await $api.delete(`${endPoints.PIPE_STATUS + data.pipeStatusId}?pipeId=${data.pipeId}`);

      return res.data;
    },
    {
      onSuccess: data => {
        showMessage(data.message, data.accept);
        qc.invalidateQueries([queryKeys.PIPE_ALL]);
        qc.invalidateQueries([queryKeys.CRM_LEAD_FILTER]);
        qc.invalidateQueries([crmConstants.queryKeys.LEAD_FILTER_V2]);
      }
    }
  );
}

export function useCreatePipe() {
  const qc = useQueryClient();

  return useMutation(
    async (
      data: {
        name: string;
        orders: number;
        id?: number;
      }[]
    ) => $api.post(endPoints.PIPE_CREATE, data),
    {
      onSuccess: () => {
        qc.invalidateQueries([queryKeys.PIPE_ALL]);
        qc.invalidateQueries([crmConstants.queryKeys.LEAD_FILTER_PARAMS]);
      }
    }
  );
}

export function useDeletePipe() {
  const qc = useQueryClient();

  return useMutation(
    async (id: number) => {
      const res = await $api.delete(endPoints.PIPE + id);

      return res.data;
    },
    {
      onSuccess: data => {
        showMessage(data.message, data.accept);
        qc.invalidateQueries([queryKeys.PIPE_ALL]);
      }
    }
  );
}

export function useGroupActionCrmViewList(pipeId: number, searchParams: SearchParamsCrm, onSuccess?: () => void) {
  const qc = useQueryClient();
  const allChecked = useAppSelector(state => state.assigmentSlice.listViewAllChecked);
  const ignoreLeadIds = useAppSelector(state => state.assigmentSlice.listViewIgnoreIds);

  return useMutation(
    async ({ deadlineStart, deadlineFinish, ...data }: GroupActions) => {
      const res = await $api.post(endPoints.LEAD_GROUP_ACTION, {
        ...data,
        deadlineStartTimestamp: dateToTimeStamp({ date: deadlineStart }),
        deadlineFinishTimestamp: dateToTimeStamp({ date: deadlineFinish }),
        leadIds: allChecked ? undefined : data?.leadIds,
        ignoreLeads: allChecked ? ignoreLeadIds : undefined,
        filter: allChecked ? { ...getCrmBody(searchParams, true), pipeId } : undefined
      });

      return res.data;
    },
    {
      onSuccess: async data => {
        showMessage(data.message, data.accept);

        await qc.invalidateQueries([queryKeys.CRM_LEAD_LIST, pipeId, JSON.stringify(searchParams)]);

        if (onSuccess) {
          onSuccess();
        }
      },
      onError: baseErrorHandler
    }
  );
}

export function useLeadGroup() {
  return useMutation(
    async (data: LeadGroup) => {
      const res = await $api.post(endPoints.LEAD_GROUP_ACTION, data);

      return res.data;
    },
    {
      // onSuccess: () => {
      // showMessage(data.message, data.accept);
      // await qc.invalidateQueries([queryKeys.CRM_LEAD_LIST]);
      // await qc.invalidateQueries([queryKeys.LEAD_DUPLICATE]);
      // await qc.invalidateQueries([queryKeys.CLIENT_VIEW]);
      // await qc.invalidateQueries([queryKeys.CRM_ONE_LID]);
      // },
      onError: baseErrorHandler
    }
  );
}

export function useImportLeads(pipeId: number, searchParams: SearchParamsCrm, onSuccess?: () => void) {
  const qc = useQueryClient();

  return useMutation(
    async (data: { pipeId: number; fileId: number; tagIds: number[] }) => {
      const res = await $api.post(endPoints.LEAD_IMPORT, data);

      return res.data;
    },
    {
      onSuccess: data => {
        showMessage(data.message, data.accept);
        qc.invalidateQueries([queryKeys.CRM_LEAD_FILTER, pipeId]);
        qc.invalidateQueries([crmConstants.queryKeys.LEAD_FILTER_V2]);
        qc.invalidateQueries([queryKeys.CRM_LEAD_LIST, pipeId, JSON.stringify(searchParams)]);

        if (onSuccess) {
          onSuccess();
        }
      },
      onError: baseErrorHandler
    }
  );
}

export function useDownloadImportLeadSample() {
  return useMutation(
    async () => {
      const res = await $api.get(endPoints.LEAD_IMPORT_EXAMPLE, {
        responseType: 'blob'
      });

      return res.data;
    },
    {
      onSuccess: data => {
        downloadFile(data, `crm-sample`, 'xlsx');
      },
      onError: baseErrorHandler
    }
  );
}

export function useExportCrm(pipeId: number, searchParams: SearchParamsCrm, onSuccess?: () => void) {
  return useMutation(
    async (params: { type: CrmExportType; leadIds?: number[]; ignoreLeadIds?: number[] }) => {
      const res = await $api.post(
        endPoints.LEAD_EXPORT,
        {
          ...(params?.leadIds && !isEmptyArr(params?.leadIds) && { leadIds: params.leadIds }),
          ...(params?.ignoreLeadIds && !isEmptyArr(params?.ignoreLeadIds) && { ignoreLeadIds: params.ignoreLeadIds }),
          filter: {
            ...getCrmBody(searchParams, true),
            pipeId
          },
          type: params?.type
        },
        {
          responseType: 'blob'
        }
      );

      return res.data;
    },
    {
      onSuccess: () => {
        if (onSuccess) {
          onSuccess();
        }
      },
      onError: baseErrorHandler
    }
  );
}
export const useMakeEmployeeAsResPonsibleForPhoneCall = () =>
  useMutation(
    async (data: { onlinePbxConfigId: number; leadResponsibleAutoChange: boolean }) => {
      const res = await $api.post(endPoints.MAKE_EMPLOYEE_RESPONSIBLE, data);

      return res.data;
    },
    {
      retry: false,
      onError: baseErrorHandler,
      onSuccess: success => showMessage(success?.message, success.accept)
    }
  );

export const useReasonsForRefusalConf = () => {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isPermissions = useAppSelector(({ userReducer: { isPermissions } }) => isPermissions);

  return useMutation(
    async (conf: boolean) => {
      if (isPermissions.PERMISSION_SETTINGS_CRM) {
        const res = await $api.post(`/v1/reasons-for-refusal/conf?saveWithoutReason=${conf}`);

        return res.data;
      }

      message.error(t('global.accessDenied'));

      return Promise.reject();
    },
    {
      retry: false,
      onError: baseErrorHandler,
      onSuccess: async success => {
        await qc.invalidateQueries({ queryKey: [queryKeys.REASONS_FOR_REFUSAL_CONF] });

        showMessage(success?.message, success.accept);
      }
    }
  );
};
